import type { ExperienceSocialConnector } from '@logto/schemas';
import classNames from 'classnames';
import { useState } from 'react';
import { isIOS, isMacOs } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import SocialLinkButton from '@/components/Button/SocialLinkButton';
import SocialTileButton from '@/components/Button/SocialTileButton';
import { getSocialBrand, withApplePriority } from '@/components/Button/social-brand';
import useLastUsedSignInMethod from '@/hooks/use-last-used-sign-in-method';
import useNativeMessageListener from '@/hooks/use-native-message-listener';
import { getLogoUrl } from '@/shared/utils/logo';

import useSocial from './use-social';

/**
 * Beyond three providers a stack of full-width rows pushes the form below the fold, so the
 * list switches to a compact logo grid instead.
 */
const stackedProviderLimit = 3;

/**
 * Balances the rows (8 → 4+4, 5 → 3+2) instead of letting auto-fit leave a ragged last row
 * when the track count changes with the viewport. Four columns still clear the 44px touch
 * target at 320px.
 */
const getTileGridColumns = (count: number) => {
  const rows = Math.ceil(count / 4);
  return Math.ceil(count / rows) === 3 ? 'grid-cols-3' : 'grid-cols-4';
};

const getLocalizedName = (name: Record<string, string>, language: string) =>
  name[language] ?? name.en;

type Props = {
  readonly className?: string;
  readonly socialConnectors?: ExperienceSocialConnector[];
  /** The "Last used" hint only applies to sign-in; a registering user has no prior sign-in. */
  readonly mode?: 'signIn' | 'register';
};

const SocialSignInList = ({
  className,
  socialConnectors: configuredConnectors = [],
  mode = 'signIn',
}: Props) => {
  const socialConnectors = withApplePriority(configuredConnectors, isIOS || isMacOs);
  const { invokeSocialSignIn, theme } = useSocial();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const lastUsedSignInMethod = useLastUsedSignInMethod();
  useNativeMessageListener();

  const [loadingConnectorId, setLoadingConnectorId] = useState<string>();

  const handleClick = async (connector: ExperienceSocialConnector) => {
    setLoadingConnectorId(connector.id);
    await invokeSocialSignIn(connector);
    setLoadingConnectorId(undefined);
  };

  const shouldUseTileGrid = socialConnectors.length > stackedProviderLimit;

  // Matched on target, not id: the target is what gets stored as the last-used method.
  const isLastUsedConnector = (target: string) =>
    mode === 'signIn' &&
    lastUsedSignInMethod?.type === 'social' &&
    lastUsedSignInMethod.target === target;

  if (shouldUseTileGrid) {
    return (
      <div
        // The tiles are logo-only, so the group needs its own name. Built from existing
        // phrases because a new key would have to land in every locale first.
        role="group"
        aria-label={t('action.sign_in_with', {
          name: socialConnectors
            .map(({ name }) => getLocalizedName(name, language))
            .join(`${t('list.separator')} `),
        })}
        className={classNames(
          'grid w-full gap-3',
          getTileGridColumns(socialConnectors.length),
          className
        )}
      >
        {socialConnectors.map((connector) => {
          const { id, name, logo: logoUrl, logoDark: darkLogoUrl, target } = connector;

          return (
            <SocialTileButton
              key={id}
              name={name}
              logo={getLogoUrl({ theme, logoUrl, darkLogoUrl })}
              brand={getSocialBrand(target)}
              isLoading={loadingConnectorId === id}
              isLastUsed={isLastUsedConnector(target)}
              onClick={() => {
                void handleClick(connector);
              }}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className={classNames('flex w-full flex-col gap-3', className)}>
      {socialConnectors.map((connector) => {
        const { id, name, logo: logoUrl, logoDark: darkLogoUrl, target } = connector;

        return (
          <SocialLinkButton
            key={id}
            name={name}
            logo={getLogoUrl({ theme, logoUrl, darkLogoUrl })}
            target={target}
            brand={getSocialBrand(target)}
            isLoading={loadingConnectorId === id}
            isLastUsed={isLastUsedConnector(target)}
            onClick={() => {
              void handleClick(connector);
            }}
          />
        );
      })}
    </div>
  );
};

export default SocialSignInList;
